import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import { useAuth } from '../../contexts/AuthContext';
import { tickerApi } from '../../services/tickerApi';
import TabBar from '../../components/Common/TabBar';
import Footer from '../../components/Footer';
import { trackTickerDetailView } from '../../utils/analytics';
import { DailyPriceTable, DailyPriceTableFullScreen, TimeframeDropdown, TickerDailyData } from '../../components/DailyPriceTable';
import { useFullScreen } from '../../hooks/useFullScreen';
import { useTickerPageMeta } from '../../hooks/useTickerPageMeta';
import { Timeframe } from '../../utils/dailyPriceUtils';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getSmallTextSizeClass } from '../../utils/fontSizeUtils';

interface TickerPriceData {
  ticker_symbol: string;
  ticker_name: string;
  daily_prices: TickerDailyData[];
}

const TickerDaily: React.FC = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { fontSize } = useFontSize();
  const { isAuthenticated } = useAuth();

  // Check for public_uuid query parameter
  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const [tickerData, setTickerData] = useState<TickerPriceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<Timeframe>('1year');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<'date' | 'close_price_adjusted' | 'num_transactions' | 'volume_adjusted'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Infinite scrolling state
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allDailyPrices, setAllDailyPrices] = useState<TickerDailyData[]>([]);
  const tableRef = useRef<HTMLDivElement>(null);
  const fullScreenTableRef = useRef<HTMLDivElement>(null);
  const currentPageRef = useRef(0);
  
  // Polling state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncPollingAttempts, setSyncPollingAttempts] = useState<number>(0);
  const [pollingLimitReached, setPollingLimitReached] = useState(false);
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // Use ref for synchronous access
  const isCheckingSyncRef = useRef(false); // Flag to prevent concurrent sync checks
  const currentAttemptRef = useRef(1); // Track current attempt number for interval callback
  
  // Ref to track previous filter values to prevent unnecessary refetches
  const prevFiltersRef = useRef<{ timeframe: string; sortColumn: string; sortDirection: string } | null>(null);
  
  // Full screen state
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  // Use full screen hook for keyboard support
  useFullScreen(isFullScreen, () => setIsFullScreen(false));

  // Reset horizontal scroll to left when switching tabs
  useEffect(() => {
    tableRef.current && (tableRef.current.scrollLeft = 0);
  }, [location.pathname]);

  // Setup scroll handler for full screen table
  useEffect(() => {
    if (isFullScreen && fullScreenTableRef.current) {
      const handleFullScreenScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = fullScreenTableRef.current!;
        if (scrollTop + clientHeight >= scrollHeight - 5 && hasMore && !loadingMore) {
          loadMoreData();
        }
      };

      fullScreenTableRef.current.addEventListener('scroll', handleFullScreenScroll);
      
      return () => {
        if (fullScreenTableRef.current) {
          fullScreenTableRef.current.removeEventListener('scroll', handleFullScreenScroll);
        }
      };
    }
  }, [isFullScreen, hasMore, loadingMore]);

  const [tickerBasicsId, setTickerBasicsId] = useState<number | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tickerName, setTickerName] = useState<string>(() => {
    const cachedName = sessionStorage.getItem(`ticker-name-${ticker}`);
    return cachedName || '';
  });
  const [tickerSymbol, setTickerSymbol] = useState<string>(() => {
    const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${ticker}`);
    return cachedSymbol ?? ticker ?? '';
  });

  // Set document title and meta description for ticker daily page
  useTickerPageMeta({
    tickerSymbol,
    tickerName,
    pageType: 'daily',
  });

  // Function to clear interval without setting stopped flag (for cleanup/reset)
  const clearPollingInterval = () => {
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }
  };

  // Function to stop polling
  const stopPolling = () => {
    pollingStoppedRef.current = true;
    clearPollingInterval();
    setIsSyncing(false);
  };

  // Function to check sync status and fetch data
  const checkSyncAndFetchData = async (attemptNumber: number = 1) => {
    if (!ticker) return;
    
    // Guard: Check if we've exceeded the attempt limit before proceeding
    if (attemptNumber > 5) {
      console.warn(`TickerDaily: Attempt ${attemptNumber} exceeds limit (5), stopping`);
      stopPolling();
      setPollingLimitReached(true);
      isCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Don't proceed if polling has been stopped (e.g., after reaching limit or 429 error)
    if (pollingStoppedRef.current) {
      console.log(`TickerDaily: checkSyncAndFetchData called but polling is stopped, ignoring`);
      isCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Prevent concurrent execution - if a sync check is already in progress, ignore this call
    if (isCheckingSyncRef.current) {
      console.log(`TickerDaily: checkSyncAndFetchData already in progress, ignoring duplicate call (attempt ${attemptNumber})`);
      return;
    }
    
    // Set flag to prevent concurrent calls
    isCheckingSyncRef.current = true;
    
    // Ensure isSyncing is true during any retry attempt to show loading animation
    // For attempt 1, isSyncing should already be set to true in useEffect
    // For attempts > 1, ensure it's set to true for the retry
    setIsSyncing(true);
    
    console.log(`TickerDaily: checkSyncAndFetchData called for ${ticker} (attempt ${attemptNumber}), setting isSyncing=true`);
    
    // Update global polling attempts for display purposes
    setSyncPollingAttempts(attemptNumber);

    try {
      console.log(`🚀 TickerDaily: About to call checkTickerSyncStatus for: ${ticker}`);
      const syncResponse = await tickerApi.checkTickerSyncStatus({
        ticker_symbols: [ticker],
      });
      
      console.log(`TickerDaily: Sync response for ${ticker}:`, syncResponse);
      
      const tickerInfo = syncResponse.data.tickers.find((t: any) => t.ticker_symbol === ticker);
      if (!tickerInfo) {
        throw new Error(`Ticker ${ticker} not found`);
      }
      
      setTickerBasicsId(tickerInfo.ticker_basics_id);
      const name = tickerInfo.ticker_name || ticker;
      const symbol = tickerInfo.ticker_symbol || ticker;
      setTickerName(name);
      setTickerSymbol(symbol);
      sessionStorage.setItem(`ticker-name-${ticker}`, name);
      sessionStorage.setItem(`ticker-symbol-${ticker}`, symbol);
      
      // Set lastUpdated from last_sync_ended_at
      if (tickerInfo.last_sync_ended_at) {
        const date = typeof tickerInfo.last_sync_ended_at === 'string' 
          ? new Date(tickerInfo.last_sync_ended_at) 
          : tickerInfo.last_sync_ended_at;
        if (date instanceof Date && !isNaN(date.getTime())) {
          setLastUpdated(date);
        }
      }
      
      // Check if ticker is synced
      if (tickerInfo.is_synced) {
        console.log(`TickerDaily: ${ticker} is synced, fetching data`);
        
        // Fetch daily prices data
        const response = await tickerApi.getTickerDailyPrices(ticker, 0, 50, timeframe, sortColumn, sortDirection);
        setTickerData(response.data);
        setAllDailyPrices(response.data.daily_prices);
        setHasMore(response.meta.hasMore);
        setCurrentPage(0);
        currentPageRef.current = 0;
        
        // Stop polling since we got the data
        stopPolling();
        setLoading(false);
        setError(null);
        isCheckingSyncRef.current = false; // Reset flag
      } else {
        // If we've reached the polling limit, try to fetch data anyway
        if (attemptNumber >= 5) {
          console.warn(`Sync polling limit reached (5 attempts) for ticker: ${ticker}. Attempting to fetch data anyway.`);
          setPollingLimitReached(true);
          stopPolling();
          
          // Still try to fetch daily prices data even though sync status is false
          try {
            const response = await tickerApi.getTickerDailyPrices(ticker, 0, 50, timeframe, sortColumn, sortDirection);
            setTickerData(response.data);
            setAllDailyPrices(response.data.daily_prices);
            setHasMore(response.meta.hasMore);
            setCurrentPage(0);
            currentPageRef.current = 0;
            setLoading(false);
            setError(null);
            console.log(`TickerDaily: Successfully fetched data for ${ticker} despite sync status being false`);
          } catch (fetchErr) {
            console.error(`TickerDaily: Failed to fetch data for ${ticker} after polling limit:`, fetchErr);
            setError(fetchErr instanceof Error ? fetchErr.message : 'Failed to fetch ticker data');
            setLoading(false);
          }
          isCheckingSyncRef.current = false; // Reset flag
        } else {
          // Double-check we haven't stopped polling before setting up interval
          if (pollingStoppedRef.current) {
            console.log(`TickerDaily: Polling stopped, not setting up interval`);
            isCheckingSyncRef.current = false; // Reset flag before returning
            return;
          }
          
          console.log(`TickerDaily: ${ticker} is not synced yet (attempt ${attemptNumber}/5), continuing to poll`);
          setIsSyncing(true);
          
          // Update the current attempt ref so the interval callback knows the next attempt number
          currentAttemptRef.current = attemptNumber;
          
          // Continue polling with 2 second delay
          const pollingDelay = 2000; // 2 seconds
          
          // Clear any existing interval before creating a new one
          // This ensures we always use the latest attempt number
          clearPollingInterval();
          
          console.log(`Starting sync polling with ${pollingDelay}ms delay`);
          const interval = setInterval(() => {
            // Check if polling was stopped or we've reached the limit
            if (pollingStoppedRef.current) {
              clearInterval(interval);
              syncPollingIntervalRef.current = null;
              return;
            }
            
            // Get the next attempt number from the ref
            const nextAttempt = currentAttemptRef.current + 1;
            
            // Check if we've exceeded the limit
            if (nextAttempt > 5) {
              console.log(`TickerDaily: Reached attempt limit (5), stopping interval`);
              clearInterval(interval);
              syncPollingIntervalRef.current = null;
              pollingStoppedRef.current = true;
              return;
            }
            
            // Update the ref for the next iteration
            currentAttemptRef.current = nextAttempt;
            
            // Make the sync check call
            checkSyncAndFetchData(nextAttempt);
          }, pollingDelay);
          
          syncPollingIntervalRef.current = interval;
          
          // Reset flag after setting up interval
          isCheckingSyncRef.current = false;
        }
      }
    } catch (err) {
      console.error(`TickerDaily: Error in checkSyncAndFetchData:`, err);
      
      // Check if it's a 429 rate limit error
      const isRateLimitError = 
        (axios.isAxiosError(err) && err.response?.status === 429) ||
        (err instanceof Error && (err.message.includes('429') || err.message.includes('rate limit') || err.message.includes('Rate limit')));
      
      if (isRateLimitError) {
        console.warn(`TickerDaily: Rate limit (429) detected for ${ticker}. Stopping polling and attempting to fetch data anyway.`);
        stopPolling();
        setPollingLimitReached(true);
        
        // Still try to fetch daily prices data even after rate limit error
        try {
          const response = await tickerApi.getTickerDailyPrices(ticker, 0, 50, timeframe, sortColumn, sortDirection);
          setTickerData(response.data);
          setAllDailyPrices(response.data.daily_prices);
          setHasMore(response.meta.hasMore);
          setCurrentPage(0);
          currentPageRef.current = 0;
          setLoading(false);
          setError(null);
          console.log(`TickerDaily: Successfully fetched data for ${ticker} despite rate limit error`);
        } catch (fetchErr) {
          console.error(`TickerDaily: Failed to fetch data for ${ticker} after rate limit error:`, fetchErr);
          setError('Rate limit exceeded. Please try again later.');
          setLoading(false);
        }
        isCheckingSyncRef.current = false; // Reset flag
      } else {
        // For other errors, set error state normally (show 404 for invalid/non-existent ticker)
        const rawMessage = err instanceof Error ? err.message : 'Failed to fetch ticker data';
        const isTickerNotFound = /no valid ticker|valid tickers found/i.test(rawMessage);
        setError(isTickerNotFound ? '404 - Ticker not found' : rawMessage);
        stopPolling();
        setLoading(false);
        isCheckingSyncRef.current = false; // Reset flag
      }
    }
  };

  // Track ticker detail view
  useEffect(() => {
    if (ticker) {
      trackTickerDetailView(ticker.toUpperCase(), 'daily');
    }
  }, [ticker]);

  // Separate useEffect for ticker change (initial sync check)
  useEffect(() => {
    if (!ticker) return;

    // Reset polling state when ticker changes
    pollingStoppedRef.current = false;
    setPollingLimitReached(false);
    setSyncPollingAttempts(0);
    currentAttemptRef.current = 1; // Reset attempt counter
    isCheckingSyncRef.current = false; // Reset concurrent check flag
    clearPollingInterval(); // Clear any existing interval without setting stopped flag
    prevFiltersRef.current = null; // Reset filter tracking when ticker changes

    // Clear existing data when starting a new ticker
    setAllDailyPrices([]);
    setTickerData(null);

    // Set syncing state immediately to show loading animation
    setIsSyncing(true);

    // Start the polling process - only when ticker changes
    setLoading(true);
    checkSyncAndFetchData(1);
  }, [ticker]); // Only depend on ticker, not filters

  // Separate useEffect for filter changes (just refetch data, don't check sync)
  useEffect(() => {
    // Skip if still loading initial data, syncing, or no data yet
    if (!ticker || loading || !tickerData || isSyncing) {
      return;
    }
    
    // Check if filters actually changed (skip initial mount when prevFiltersRef is null)
    const currentFilters = { timeframe, sortColumn, sortDirection };
    if (prevFiltersRef.current && 
        prevFiltersRef.current.timeframe === currentFilters.timeframe &&
        prevFiltersRef.current.sortColumn === currentFilters.sortColumn &&
        prevFiltersRef.current.sortDirection === currentFilters.sortDirection) {
      return; // Filters haven't changed, skip refetch
    }
    
    // Update ref for next comparison
    prevFiltersRef.current = currentFilters;
    
    // If we already have data, just refetch with new filters
    // Don't check sync status again
    const refetchWithFilters = async () => {
      try {
        console.log(`TickerDaily: Refetching data with new filters: timeframe=${timeframe}, sortColumn=${sortColumn}, sortDirection=${sortDirection}`);
        const response = await tickerApi.getTickerDailyPrices(ticker, 0, 50, timeframe, sortColumn, sortDirection);
        setTickerData(response.data);
        setAllDailyPrices(response.data.daily_prices);
        setHasMore(response.meta.hasMore);
        setCurrentPage(0);
        currentPageRef.current = 0;
      } catch (err) {
        console.error('TickerDaily: Error refetching with new filters:', err);
        // Don't set error state for filter changes, just log
      }
    };

    refetchWithFilters();
  }, [ticker, timeframe, sortColumn, sortDirection, loading, tickerData, isSyncing]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Handle clicking outside dropdown to close it - handled by TimeframeDropdown component

  // Load more data function
  const loadMoreData = useCallback(async () => {
    if (!ticker || loadingMore || !hasMore) {
      return;
    }

    try {
      setLoadingMore(true);
      const nextPage = currentPageRef.current + 1;
      currentPageRef.current = nextPage;
      setCurrentPage(nextPage);
      
      const response = await tickerApi.getTickerDailyPrices(ticker, nextPage, 50, timeframe, sortColumn, sortDirection);

      setAllDailyPrices(prev => [...prev, ...response.data.daily_prices]);
      setHasMore(response.meta.hasMore);
    } catch (err) {
      console.error('Failed to load more data:', err);
      // Revert currentPage on error
      currentPageRef.current = currentPageRef.current - 1;
      setCurrentPage(currentPageRef.current);
    } finally {
      setLoadingMore(false);
    }
  }, [ticker, loadingMore, hasMore, timeframe, sortColumn, sortDirection]);

  // Set up scroll listener when table is rendered
  useEffect(() => {
    const handleScroll = () => {
      if (!tableRef.current) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // Load when 100px from bottom

      if (isNearBottom && hasMore && !loadingMore) {
        loadMoreData();
      }
    };

    // Wait for the table to be rendered
    const checkAndSetupScroll = () => {
      if (tableRef.current) {
        tableRef.current.addEventListener('scroll', handleScroll);
        return true;
      }
      return false;
    };

    // Try immediately
    if (!checkAndSetupScroll()) {
      // If not available, use requestAnimationFrame to wait for next render cycle
      const rafId = requestAnimationFrame(() => {
        if (!checkAndSetupScroll()) {
          // If still not available, try again after a short delay
          setTimeout(checkAndSetupScroll, 50);
        }
      });

      return () => {
        cancelAnimationFrame(rafId);
        if (tableRef.current) {
          tableRef.current.removeEventListener('scroll', handleScroll);
        }
      };
    }

    return () => {
      if (tableRef.current) {
        tableRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [loadMoreData, hasMore, loadingMore, currentPage, allDailyPrices.length]);

  const handleBackClick = () => {
    if (publicUuid) {
      navigate(`/daily?public_uuid=${publicUuid}`);
    } else {
      navigate('/daily');
    }
  };

  const handleSort = (column: 'date' | 'close_price_adjusted' | 'num_transactions' | 'volume_adjusted') => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      // New column, start with descending
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Formatting functions moved to dailyPriceUtils

  const tabs = (
    <TabBar 
      activeTab="daily" 
      ticker={ticker}
      publicUuid={publicUuid}
    />
  );

  // Timeframe dropdown component - using shared component
  const timeframeDropdown = (
    <TimeframeDropdown
      timeframe={timeframe}
      onTimeframeChange={(newTimeframe) => {
        setTimeframe(newTimeframe);
        setIsDropdownOpen(false);
      }}
      isOpen={isDropdownOpen}
      onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
      onClose={() => setIsDropdownOpen(false)}
    />
  );


  if (error) {
    return (
      <>
      <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
        <Header sticky={false} />
        {publicUuid && !isAuthenticated && <PublicListPromoBar />}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Error</h2>
              <p className="text-gray-600 dark:text-text-secondary">{error}</p>
            </div>
          </div>
        </main>
        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>
      </>
    );
  }


  return (
    <>
    <style dangerouslySetInnerHTML={{
      __html: `
        /* Sticky header top border - using a class-based approach */
        table thead.sticky-header-border::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead.sticky-header-border::before {
          background-color: rgb(55, 65, 81);
        }
        
        /* Sticky header bottom border - using a class-based approach */
        table thead.sticky-header-border::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead.sticky-header-border::after {
          background-color: rgb(55, 65, 81);
        }
      `
    }} />
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      {!isFullScreen && <Header sticky={false} />}
      {!isFullScreen && publicUuid && !isAuthenticated && <PublicListPromoBar />}

      <main className="px-1 lg:px-2 xl:px-4 pt-4 sm:pt-8 pb-4 sm:pb-8">
        <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md shadow-lg overflow-hidden">
          {/* Back button */}
          <div className="p-4">
            <button
              onClick={handleBackClick}
              className={`flex items-center ${getTextSizeClass(fontSize)} text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors`}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to List
            </button>
          </div>
          
          {/* Title */}
          <div className="px-4 pb-4">
            <h1 className="text-xl font-semibold text-text-light-primary dark:text-text-dark-primary">
              {tickerName || tickerData?.ticker_name || 'Loading'} ({tickerSymbol})
            </h1>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-border-color">
            <nav className="px-4">
              {tabs}
            </nav>
          </div>

          {/* Timeframe Dropdown */}
          <div className="px-4 h-16 flex items-center">
            {timeframeDropdown}
          </div>

          {/* Historical Price Table - using shared component */}
          <DailyPriceTable
            data={allDailyPrices}
            loading={loading}
            loadingMore={loadingMore}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            onFullScreenClick={() => setIsFullScreen(true)}
            tableRef={tableRef}
            isSyncing={isSyncing}
          />
          
          {/* Status Bar */}
          <div className={`px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
            <div className="flex items-center gap-4">
              {loading && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                  <span>Loading...</span>
                </div>
              )}
              {lastUpdated && !loading && (
                <span>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}</span>
              )}
            </div>
          </div>
        </div>
      </main>
      
      {/* Full Screen Overlay - using shared component */}
      {isFullScreen && (
        <DailyPriceTableFullScreen
          data={allDailyPrices}
          loading={loading}
          loadingMore={loadingMore}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          onClose={() => setIsFullScreen(false)}
          tableRef={fullScreenTableRef}
          isSyncing={isSyncing}
        />
      )}
      
      {/* Footer */}
      <div className="mt-4 sm:mt-8">
        <Footer />
      </div>
    </div>
    </>
  );
};

export default TickerDaily;